import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, Lightbulb } from 'lucide-react';
import { PerformanceLoopWidget } from '../components/PerformanceLoopWidget';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PerformanceLoopPage() {
  const [learnings, setLearnings] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/performance-loop/learnings?days=60`)
      .then(r => setLearnings(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="performance-loop-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-emerald-brand" />
          Performance Loop
          <PageInfoTooltip pageKey="performance-loop" />
        </h1>
        <p className="text-gray-medium mt-1">
          Konnect learns from what your audience actually engages with — top winners influence next round&apos;s generation.
        </p>
      </div>

      <PerformanceLoopWidget />

      {/* Konnect Learnings — prompt-augmentation hints distilled from the data */}
      {learnings?.data_available && (
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-indigo-200" data-testid="holo-learnings">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-indigo-600" />
              What Konnect Learned
              <Badge className="bg-indigo-100 text-indigo-700 text-[10px] ml-auto">Auto-applied to next gen</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-800">
            {learnings.prompt_hint && (
              <div className="bg-white/70 rounded-lg p-3 border border-indigo-100">
                <p className="text-xs text-indigo-700 uppercase tracking-wide mb-1 font-semibold">Prompt augmentation</p>
                <p className="text-sm italic leading-relaxed">{`"${learnings.prompt_hint}"`}</p>
              </div>
            )}
            {learnings.winning_formats?.length > 0 && (
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1 font-semibold">Winning formats</p>
                <div className="flex flex-wrap gap-1.5">
                  {learnings.winning_formats.map(f => (
                    <Badge key={f.format} className="bg-emerald-100 text-emerald-800 border-emerald-200">
                      {f.format} · {f.avg_score}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {learnings.weak_formats?.length > 0 && (
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1 font-semibold">Avoid these</p>
                <div className="flex flex-wrap gap-1.5">
                  {learnings.weak_formats.map(f => (
                    <Badge key={f.format} variant="outline" className="text-rose-700 border-rose-200">
                      {f.format} · {f.avg_score}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
